import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import * as itemActions from 'tradera/state/items/actions';
import { itemStates } from './item-states';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import { Button } from '../../../../components/atoms/button/button';
import type { WishListItemCardViewModel } from 'tradera/state/services/types/webapi-member-generated';
import { useAppDispatch } from 'tradera/state/hooks';
import { SOURCE_SCREEN } from 'tradera/services/google-tagmanager/constants';

type Props = {
    item: WishListItemCardViewModel;
    grayedOut?: boolean;
};

export const ActionButton = ({ item, grayedOut }: Props) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const dispatch = useAppDispatch();

    const handleClickToVip = () => {
        GtmService.trackAction('My Tradera', 'Wishlist', 'Go to VIP');
    };

    switch (item.itemState) {
        case itemStates.bidderActiveLeading:
            return (
                <Button
                    theme="primary"
                    disabled={grayedOut}
                    href={toLocalizedUrl(item.itemUrl)}
                    onClick={() => {
                        handleClickToVip();
                    }}>
                    {t('myt_actions_listItemRaiseMaxBidAction')}
                </Button>
            );
        case itemStates.bidderActiveReservedPriceNotReached:
        case itemStates.bidderActiveOverbidden:
        case itemStates.generalActive:
            return (
                <Button
                    theme="primary"
                    disabled={grayedOut}
                    href={toLocalizedUrl(item.itemUrl)}
                    onClick={() => {
                        handleClickToVip();
                    }}>
                    {t('myt_actions_listItemBidAction')}
                </Button>
            );
        case itemStates.buyItNowActive:
        case itemStates.shopItemActive:
            return (
                <>
                    <Button
                        theme="primary"
                        disabled={grayedOut}
                        href={toLocalizedUrl(item.itemUrl)}
                        onClick={() => {
                            handleClickToVip();
                        }}>
                        {t('myt_actions_listItemBuyAction')}
                    </Button>
                    {item.isAvailableForOffers && (
                        <Button
                            theme="primary"
                            variant="outline"
                            disabled={grayedOut}
                            href={toLocalizedUrl(item.itemUrl)}
                            onClick={() => {
                                handleClickToVip();
                            }}>
                            {t('myt_actions_listItemPurchaseOfferAction')}
                        </Button>
                    )}
                </>
            );
        case itemStates.bidderAuctionRemoved:
        case itemStates.bidderBidRemoved:
        case itemStates.bidderEndedReservedPriceNotReached:
        case itemStates.bidderEndedNotWon:
        case itemStates.buyItNowEnded:
        case itemStates.generalEnded:
        case itemStates.shopItemEnded:
            return (
                <Button
                    theme="primary"
                    variant="outline"
                    disabled={grayedOut}
                    onClick={() =>
                        dispatch(
                            itemActions.wishlist(item.itemId, {
                                sectionGaName: 'My Tradera',
                                gaSourceScreen: SOURCE_SCREEN.WISHLIST,
                                value: false,
                            }),
                        )
                    }>
                    {t('myt_actions_listItemWishListDeleteAction')}
                </Button>
            );
        case itemStates.bidderEndedWon:
        case itemStates.shopItemEndedWon:
        case itemStates.buyItNowEndedWon:
            return (
                <Button
                    theme="primary"
                    variant="outline"
                    disabled={grayedOut}
                    href={toLocalizedUrl('/my/purchases')}>
                    {t('myt_actions_listGotoPurchasesAction')}
                </Button>
            );
        default:
            throw new Error('Unknown item state');
    }
};
